import React, { useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { MenuItem, TextField, Box, Card, Grid, CardActions, Alert } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton/IconButton";
import Typography from "@mui/material/Typography/Typography";
import { IoWarningOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import { getRollsForChild, rollOverSchedulesUpdate } from "../../store/modules/rollOver/rollOverActions";
import { RollOverScheduleHttpResponse, RollOverStatusHttpRequest } from "../../store/parentApi";
import { RootState } from "../../store/reducers";
import { Child } from "../../types/models";
import { getStyleColor } from "../../utils/helpers";
import { isChildBlocked } from "../../utils/medicalConditionsHelper";
import { ButtonPrimary } from "../Common/Buttons/Buttons";
import RouteLink from "../Common/Links/RouteLink";
import Spacer from "../Common/Spacers/Spacers";

interface IProps {
  schedules: RollOverScheduleHttpResponse[];
  handleClose: (showSaved: boolean) => void;
  open: boolean;
  year: number;
  isNew: boolean;
  childrenWithoutSchedule: Child[];
}

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "white",
  },
  columnText: {
    fontSize: 16,
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  title: {
    marginTop: theme.spacing(2),
  },
  select: {
    "&:focus": {
      backgroundColor: "white",
    },
  },
  dayAvailable: {
    backgroundColor: "white",
    border: "1px solid black",
    color: "black",
  },
  daySelected: {
    backgroundColor: "var(--youroshc-color-primary)",
    border: "1px solid black",
    color: "black",
  },
  dayDisabled: {
    backgroundColor: "#DDDDDD",
    border: "2px solid white",
    color: "white",
  },
  section: {
    border: "2px solid var(--youroshc-color-primary)",
    borderRadius: "5px",
    backgroundColor: "var(--youroshc-color-primary-light)",
  },
  week: {
    fontWeight: "bold",
    display: "inline-block",
    marginTop: "10px",
    marginLeft: "10px",
  },
}));

type DayKeysWeek1 = "mon1" | "tue1" | "wed1" | "thu1" | "fri1";
type DayKeysWeek2 = "mon2" | "tue2" | "wed2" | "thu2" | "fri2";
type DayKeys = DayKeysWeek1 | DayKeysWeek2;

export function ScheduleEditorDialog(props: IProps) {
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const { year, handleClose, open, isNew, childrenWithoutSchedule } = props;
  const [schedules, setSchedules] = React.useState<RollOverScheduleHttpResponse[]>(!isNew ? props.schedules : []);
  const [childWithoutSchedule, setChildWithoutSchedule] = React.useState<Child | null>(null);
  const { isSaving, isLoadingRollsForChild, isLoadingRollsForChildError, isRollsForChildLoaded, newRollOverSchedules } =
    useSelector((state: RootState) => ({
      isSaving: state.rollOverReducer.isSaving,
      isLoadingRollsForChild: state.rollOverReducer.isLoadingRollsForChild,
      isLoadingRollsForChildError: state.rollOverReducer.isLoadingRollsForChildError,
      isRollsForChildLoaded: state.rollOverReducer.isRollsForChildLoaded,
      newRollOverSchedules: state.rollOverReducer.newRollOverSchedules,
    }));

  const { customerAccountId, documents: customerDocuments } = useSelector((state: RootState) => ({
    customerAccountId: state.customer.customer?.customerAccountId,
    documents: state.customer.customer?.documents ?? [],
  }));

  const toggleDay = (rollId: number, fieldName: keyof RollOverScheduleHttpResponse) => {
    var updatedSchedules = schedules.map((x) =>
      x.rollId === rollId ? ({ ...x, [fieldName]: !x[fieldName] } as RollOverScheduleHttpResponse) : x
    );
    setSchedules(updatedSchedules);
  };

  useEffect(() => {
    if (childWithoutSchedule !== null) {
      getRollsForChild(dispatch, childWithoutSchedule);
    }
  }, [childWithoutSchedule]);

  useEffect(() => {
    if (isNew && newRollOverSchedules !== null) {
      setSchedules(newRollOverSchedules);
    }
  }, [newRollOverSchedules]);

  function rollOverSchedulesOpt() {
    rollOverSchedulesUpdate(
      dispatch,
      customerAccountId!,
      schedules.map((schedule) => ({
        childId: schedule.childId,
        rollId: schedule.rollId,
        mon1: schedule.mon1,
        tue1: schedule.tue1,
        wed1: schedule.wed1,
        thu1: schedule.thu1,
        fri1: schedule.fri1,
        mon2: schedule.mon2,
        tue2: schedule.tue2,
        wed2: schedule.wed2,
        thu2: schedule.thu2,
        fri2: schedule.fri2,
        forYear: schedule.forYear,
        rollOverStatus: RollOverStatusHttpRequest.OptOut,
      })),
      () => {
        handleClose(true);
      }
    );
  }

  const days1: DayKeysWeek1[] = ["mon1", "tue1", "wed1", "thu1", "fri1"];
  const days2: DayKeysWeek2[] = ["mon2", "tue2", "wed2", "thu2", "fri2"];
  function renderDaySelections(sch: RollOverScheduleHttpResponse, days: DayKeys[]) {
    return days.map((day, index) => (
      <DaySelection
        key={day}
        title={["M", "T", "W", "T", "F"][index]}
        selected={sch[day]}
        dayAvailable={classes.dayAvailable}
        daySelected={classes.daySelected}
        onClick={() => toggleDay(sch.rollId, day)}
      />
    ));
  }

  var hasFortnightly = schedules.some((x) => x.frequencyTypeId === 2);
  const noDaySelected = schedules.every((x) => {
    const days1 = [x.mon1, x.tue1, x.wed1, x.thu1, x.fri1];
    const days2 = x.frequencyTypeId === 2 ? [x.mon2, x.tue2, x.wed2, x.thu2, x.fri2] : [];
    return days1.every((day) => !day) && days2.every((day) => !day);
  });

  const hasDuplicateDaysPerCareType = schedules.some((x) => {
    const days1: DayKeysWeek1[] = ["mon1", "tue1", "wed1", "thu1", "fri1"];
    const days2: DayKeysWeek2[] = x.frequencyTypeId === 2 ? ["mon2", "tue2", "wed2", "thu2", "fri2"] : [];
    const allDays: DayKeys[] = [...days1, ...days2];

    return allDays.some(
      (day) => schedules.filter((s) => s.careTypeId % 100 === x.careTypeId % 100 && s[day]).length > 1
    );
  });

  const secureDisabled = noDaySelected || hasDuplicateDaysPerCareType;

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      sx={{ borderRadius: 33, p: 0 }}
      maxWidth={hasFortnightly ? "md" : "sm"}
    >
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="primary" />
      </Backdrop>

      <DialogTitle id="alert-dialog-title">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", alignContent: "center" }}>
          <ErrorIcon
            sx={{
              mx: "auto",
              background: "black",
              color: getStyleColor("--youroshc-color-primary"),
              borderRadius: "50%",
              width: { xs: 25, md: 32 },
              height: { xs: 25, md: 32 },
            }}
          />
        </Box>
        <IconButton aria-label="close" className={classes.closeButton} onClick={() => handleClose(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={0} justifyContent="center" alignItems="center">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              alignContent: "center",
              width: "100%",
            }}
          >
            <Typography className={classes.columnText} variant="subtitle2" align="center" justifyContent="center">
              Please select which days you would like to book in {year}.
            </Typography>
            {isNew === true && (
              <>
                <Spacer margin={5} />
                <TextField
                  select
                  id="selectChild"
                  label="Select child"
                  onChange={(e) => {
                    const selectedChildId = Number(e.target.value);
                    const selectedChild = childrenWithoutSchedule.find((c) => c.childId === selectedChildId);

                    if (selectedChild) {
                      setChildWithoutSchedule(selectedChild);
                    }
                  }}
                  variant="filled"
                  fullWidth
                  required
                >
                  <MenuItem value="">Please select</MenuItem>
                  {[...childrenWithoutSchedule]
                    .sort((a, b) => {
                      const firstNameCompare = a.firstName!.localeCompare(b.firstName!);
                      if (firstNameCompare !== 0) return firstNameCompare;
                      return a.lastName!.localeCompare(b.lastName!);
                    })
                    .map((c, index) => {
                      const isDisabled =
                        c.isSuspended === true ||
                        isChildBlocked(
                          c,
                          customerDocuments.filter((x) => x.childId === c.childId)
                        );

                      return (
                        <MenuItem value={c.childId} key={index} disabled={isDisabled}>
                          {c.firstName} {c.lastName}
                        </MenuItem>
                      );
                    })}
                </TextField>
                <Grid container justifyContent="flex-end" sx={{ mt: 1, paddingBottom: 1 }}>
                  <Grid item>
                    <RouteLink to="/add-child">Don't see your child here? Add new child.</RouteLink>
                  </Grid>
                </Grid>
                <Spacer margin={10} />
              </>
            )}
            {isRollsForChildLoaded && schedules.length === 0 && <p>No rolls available</p>}
          </Box>
          <Spacer margin={5} />
          <Backdrop className={classes.backdrop} open={isLoadingRollsForChild}>
            <CircularProgress color="primary" />
          </Backdrop>
          {!isLoadingRollsForChild &&
            schedules.map((sch) => (
              <>
                <Grid container justifyContent="center" alignItems="center">
                  <Typography variant="subtitle2" fontWeight={"bold"} align="center">
                    {sch.rollName}
                  </Typography>
                </Grid>

                <Grid container justifyContent="center" alignItems="center">
                  {sch.frequencyTypeId === 1 && (
                    <Card className={classes.section}>
                      <CardActions>
                        <Grid container>{renderDaySelections(sch, days1 as DayKeys[])}</Grid>
                      </CardActions>
                    </Card>
                  )}

                  {sch.frequencyTypeId === 2 && (
                    <Grid container spacing={2}>
                      <Grid item md={6} xs={12}>
                        <Box
                          display="flex"
                          sx={{
                            justifyContent: { xs: "center", md: "right" },
                          }}
                        >
                          <Card className={classes.section}>
                            <span className={classes.week}>Week A</span>
                            <CardActions>{renderDaySelections(sch, days1 as DayKeys[])}</CardActions>
                          </Card>
                        </Box>
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <Box
                          display="flex"
                          sx={{
                            justifyContent: { xs: "center", md: "left" },
                          }}
                        >
                          <Card className={classes.section}>
                            <span className={classes.week}>Week B</span>
                            <CardActions>{renderDaySelections(sch, days2 as DayKeys[])}</CardActions>
                          </Card>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Spacer margin={5} />
              </>
            ))}

          <Spacer margin={10} />

          <Grid container justifyContent="center" spacing={2}>
            {secureDisabled && hasDuplicateDaysPerCareType && (
              <Alert
                severity="warning"
                sx={{
                  mt: 2,
                  ml: 1,
                  borderRadius: 2,
                }}
              >
                <Typography className={classes.columnText} variant="subtitle2" align="left">
                  Oops! It looks like the schedules you're trying to add clash with each other. Please review your
                  schedules and make sure there are no overlaps before confirming.
                </Typography>
              </Alert>
            )}
            <Box display="flex" justifyContent="left">
              <ButtonPrimary
                style={{
                  backgroundColor: getStyleColor("--youroshc-color-primary"),
                  color: "black",
                  fontWeight: "bold",
                }}
                onClick={rollOverSchedulesOpt}
                disabled={secureDisabled}
              >
                Secure My {year} Schedule
              </ButtonPrimary>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );

  interface IDaySelectionProps {
    title: string;
    selected: boolean;
    onClick: () => void;
    daySelected?: string;
    dayAvailable?: string;
  }

  function DaySelection(props: IDaySelectionProps) {
    const { title, selected, onClick, dayAvailable, daySelected } = props;
    return (
      <IconButton
        onClick={() => {
          onClick();
        }}
        component="span"
      >
        <Avatar sx={{ width: 40, height: 40 }} className={selected ? daySelected : dayAvailable}>
          {title}
        </Avatar>
      </IconButton>
    );
  }
}
